"use client";

import { LayoutGrid, List } from "@/components/icons";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatting/numbers";
import { Button } from "@/components/ui/button";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import { useListingBrowseContext } from "./context/listing-browse-context";
import { buildResultsHeading } from "./results-heading";
import { ResultsOrderLabel } from "./results-order-label";
import { CatalogueResultsHeading } from "./catalogue-shell";

interface ResultsHeaderProps {
  totalCount: number | null;
  loadedCount: number;
  isExpandedSearch: boolean;
  isLoading: boolean;
  onToggleColumnEditor: () => void;
}

function ResultsCount({
  totalCount,
  loadedCount,
  isLoading,
}: Pick<ResultsHeaderProps, "totalCount" | "loadedCount" | "isLoading">) {
  if (isLoading) return <>Loading listings…</>;
  if (totalCount === null) {
    return <>{formatNumber(loadedCount)}+ listings</>;
  }
  return <>{formatNumber(totalCount)} listings</>;
}

function ViewModeToggle() {
  const { viewMode, setViewMode } = useListingBrowseContext();
  const modes = [
    { value: "grid" as const, label: "Grid view", icon: LayoutGrid },
    { value: "table" as const, label: "Table view", icon: List },
  ];

  return (
    <div className="hidden items-center overflow-hidden rounded-sm border border-border sm:flex">
      {modes.map((mode) => (
        <button
          key={mode.value}
          type="button"
          onClick={() => setViewMode(mode.value)}
          aria-pressed={viewMode === mode.value}
          title={mode.label}
          className={cn(
            "flex h-8 w-8 items-center justify-center transition-colors",
            viewMode === mode.value
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
          )}
        >
          <mode.icon className="h-4 w-4" aria-hidden />
        </button>
      ))}
    </div>
  );
}

function hasNarrowingFilter(browse: ReturnType<typeof useListingBrowseContext>): boolean {
  return (
    browse.intentFilter !== "all" ||
    browse.stateCode !== null ||
    browse.conditions.length > 0 ||
    browse.specFilters.length > 0 ||
    browse.searchQuery !== "" ||
    browse.priceRange.min !== null ||
    browse.priceRange.max !== null ||
    browse.listedAfterDays !== null
  );
}

function useResultsHeading(isExpandedSearch: boolean): string {
  const browse = useListingBrowseContext();
  const { productType, intentFilter } = browse;
  const { location, quantity } = useHeroMapContext();
  const isNarrowed = location !== null || quantity > 1 || hasNarrowingFilter(browse);
  if (browse.pageTitle && !isNarrowed) return browse.pageTitle;
  return buildResultsHeading({
    productType,
    intentFilter,
    city: isExpandedSearch ? undefined : location?.city,
    stateAbbr: isExpandedSearch ? undefined : location?.state,
  });
}

function ResultsSubtitle(
  props: Pick<
    ResultsHeaderProps,
    "totalCount" | "loadedCount" | "isExpandedSearch" | "isLoading"
  >,
) {
  return (
    <>
      <ResultsCount {...props} />
      {props.isExpandedSearch ? (
        <span className="ml-2 not-tabular-nums">
          — nothing in your radius, showing the nearest available
        </span>
      ) : null}
    </>
  );
}

export function ResultsHeader({
  totalCount,
  loadedCount,
  isExpandedSearch,
  isLoading,
  onToggleColumnEditor,
}: ResultsHeaderProps) {
  const { viewMode } = useListingBrowseContext();
  const heading = useResultsHeading(isExpandedSearch);

  return (
    <CatalogueResultsHeading
      title={heading}
      subtitle={
        <ResultsSubtitle
          totalCount={totalCount}
          loadedCount={loadedCount}
          isExpandedSearch={isExpandedSearch}
          isLoading={isLoading}
        />
      }
      actions={
        <>
          <ResultsOrderLabel />
          <ViewModeToggle />
          {viewMode === "table" ? (
            <Button
              type="button"
              variant="outline"
              size="compact"
              onClick={onToggleColumnEditor}
              className="hidden text-muted-foreground sm:inline-flex"
            >
              Columns
            </Button>
          ) : null}
        </>
      }
    />
  );
}
